"use client"

import { Badge } from "@/components/ui/badge"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import type { FieldConfiguration } from "@/types/workflow"
import { useLanguage } from "@/context/language-context"
import React from "react"

interface CollectionCellProps {
  value: any[]
  column: FieldConfiguration
  fieldInteraction?: any
  renderItem?: (item: any, label: string, index: number) => React.ReactNode
}

export function CollectionCell({ 
  value, 
  column, 
  fieldInteraction,
  renderItem 
}: CollectionCellProps) {
  const { language } = useLanguage()
  
  // Filter active items (handle both IsDeleted and isDeleted)
  let items = Array.isArray(value) 
    ? value.filter(item => !item?.IsDeleted && !item?.isDeleted) 
    : []
  
  if (items.length === 0) {
    return <div className="px-2 py-1 text-gray-400 text-sm">—</div>
  }
  
  // Special sorting for Alerts column (Error > Warning > Information)
  if (column.columnCode?.toLowerCase() === 'alerts') {
    items = [...items].sort((a, b) => {
      const priority: Record<string, number> = { 
        Error: 0, error: 0,
        Warning: 1, warning: 1,
        Information: 2, information: 2
      };
      const typeA = a.type || a.Type || 'Information';
      const typeB = b.type || b.Type || 'Information';
      return (priority[typeA] ?? 999) - (priority[typeB] ?? 999);
    });
  }

  // Get translation helper with case-insensitive property access
  const getItemLabel = (item: any): string => {
    // Extract code from item (handle both direct values and objects)
    let code: string;
    if (column.subColumnName && typeof item === 'object' && item !== null) {
      // Case-insensitive lookup for subColumnName
      const subColLower = column.subColumnName.toLowerCase();
      const foundKey = Object.keys(item).find(k => k.toLowerCase() === subColLower);
      code = foundKey ? String(item[foundKey]) : String(item);
    } else {
      code = String(item);
    }
    
    // Look up in fieldInteraction for translation
    if (fieldInteraction?.customValues) {
      const customValue = fieldInteraction.customValues.find(
        (cv: any) => {
          // Case-insensitive code comparison
          const cvCode = cv.code || cv.Code;
          return String(cvCode).toLowerCase() === String(code).toLowerCase();
        }
      );
      
      if (customValue) {
        // Handle both capitalized and lowercase property names
        const titleFr = customValue.titleFrench || customValue.TitleFrench;
        const titleEn = customValue.titleEnglish || customValue.TitleEnglish;
        
        return language === 'fr'
          ? (titleFr || titleEn || code)
          : (titleEn || code);
      }
    }
    
    return code;
  }

  // For single item, just render it
  if (items.length === 1) {
    const label = getItemLabel(items[0])
    return (
      <div className="flex items-center gap-2 px-2 py-1">
        {renderItem ? renderItem(items[0], label, 0) : (
          <Badge variant="secondary">{label}</Badge>
        )}
      </div>
    )
  }

  // For multiple items, show first + counter with popover
  const firstItem = items[0]
  const firstLabel = getItemLabel(firstItem)
  const remainingCount = items.length - 1

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-50 rounded">
          {renderItem ? renderItem(firstItem, firstLabel, 0) : (
            <Badge variant="secondary">{firstLabel}</Badge>
          )}
          <span className="text-sm text-blue-600 font-medium">
            +{remainingCount}
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-2">
          {items.map((item, idx) => {
            const label = getItemLabel(item)
            return (
              <div key={idx}>
                {renderItem ? renderItem(item, label, idx) : (
                  <Badge variant="secondary">{label}</Badge>
                )}
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
